import { Hidden } from '@mui/material'
import cn from 'classnames'
import { BetaTag } from 'src/components/global/BetaTag/BetaTag'
import { EnhancedTag } from 'src/components/global/EnhancedTag/EnhancedTag'
import { PremiumTag } from 'src/components/global/PremiumTag/PremiumTag'
import { StartupTag } from 'src/components/global/StartupTag/StartupTag'
import { ThemeOnly } from 'src/components/global/ThemeOnly'
import { LinkWithOrigin } from 'src/components/LinkWithOrigin/LinkWithOrigin'
import {
  IAlspSolutionDataFragment,
  IConsultancySolutionDataFragment,
  IProductSolutionDataFragment,
} from 'src/graphql/generated/hooks'
import { isAlspSolution, isConsultancySolution, isProductSolution } from 'src/utils/SolutionDataFragment'
import { isConsolidationProductSolution } from 'src/utils/SolutionDataFragment'

interface SolutionTitleProps {
  solution: IAlspSolutionDataFragment | IConsultancySolutionDataFragment | IProductSolutionDataFragment
}

export const SolutionTitle: React.FC<SolutionTitleProps> = ({ solution }) => {
  const isPremium = !!solution?.isPremium
  const isEnhanced = isPremium || !!solution?.isEnhanced
  const isStartup = !!solution?.isStartup
  const isBeta = !!solution?.isBeta
  const name = isAlspSolution(solution)
    ? solution?.alspData?.name
    : isConsultancySolution(solution)
      ? solution?.consultantData?.name
      : isProductSolution(solution)
        ? solution?.productData?.name
        : null
  const company = solution?.company || null
  const shouldShowCompany = company?.slug && company?.name
  const serviceName = isAlspSolution(solution)
    ? solution?.alspData?.serviceName
    : isConsultancySolution(solution)
      ? solution?.consultantData?.serviceName
      : isProductSolution(solution)
        ? solution?.productData?.tool
        : null
  const shouldShowName = serviceName && !shouldShowCompany && name !== serviceName

  const isConsolidation = isConsolidationProductSolution(solution)
  const originalTargetName = (
    <>
      {isConsolidation &&
        solution?.consolidationData?.buyer != null &&
        solution?.consolidationData?.originalTargetName != null && (
          <div className="text-[15px]">
            &nbsp;&nbsp;(Previously known as &nbsp;
            <span className="font-bold">{solution?.consolidationData?.originalTargetName}</span>)
          </div>
        )}
    </>
  )

  return (
    <div className="flex flex-col items-center md:items-start">
      <h1
        className={cn(
          'pb-[0.125rem] text-[1.5rem] font-bold font-poppins text-[#202A35]',
          isPremium || isStartup || isBeta ? 'mb-[3px]' : 'mb-[2px]',
        )}
      >
        {serviceName ? serviceName : name}
      </h1>
      <Hidden mdDown>
        <ThemeOnly theme="legaltech">
          {(isPremium || (!isPremium && isEnhanced) || isStartup || isBeta) && (
            <div
              className={cn(
                'inline-flex items-center',
                shouldShowName || shouldShowCompany ? 'mb-[19px]' : 'mb-[19px]',
              )}
            >
              {isPremium && <PremiumTag variant="yellow" />}
              {!isPremium && isEnhanced && <EnhancedTag />}
              {isStartup && <StartupTag />}
              {isBeta && <BetaTag />}
            </div>
          )}
        </ThemeOnly>
        <ThemeOnly theme="ilta">
          <div className={cn('inline-flex items-center space-x-[10px]  mb-[21px]')}>
            {(isPremium || isEnhanced) && <EnhancedTag />}
          </div>
        </ThemeOnly>
      </Hidden>
      <Hidden mdUp>
        <ThemeOnly theme="legaltech">
          {(isPremium || (!isPremium && isEnhanced) || isStartup || isBeta) && (
            <div
              className={cn(
                'inline-flex items-center',
                shouldShowName || shouldShowCompany ? 'mb-[19px]' : 'mb-[19px]',
              )}
            >
              {isPremium && <PremiumTag variant="yellow" />}
              {!isPremium && isEnhanced && <EnhancedTag />}
              {isStartup && <StartupTag />}
              {isBeta && <BetaTag />}
            </div>
          )}
        </ThemeOnly>
        <ThemeOnly theme="ilta">
          <div className={cn('inline-flex items-center space-x-[10px]  mb-[21px]')}>
            {(isPremium || isEnhanced) && <EnhancedTag />}
          </div>
        </ThemeOnly>
      </Hidden>
      {shouldShowName && (
        <h1 className=" text-[19px] font-medium font-primary flex leading-[21px] mb-[14px] text-darkgray items-center">
          by&nbsp;<span className="text-hero font-bold leading-[21px]">{name}</span> {originalTargetName}
        </h1>
      )}
      {shouldShowCompany && (
        <div className="text-[16px] font-medium font-primary flex leading-[15px] mb-[17px] text-darkgray items-center">
          <div className="flex flex-row items-center">
            by&nbsp;
            <LinkWithOrigin toUrl={'/companies/' + company?.slug} solutionName={serviceName || name || ''}>
              <span className="cursor-pointer text-base font-bold leading-[15px]">{company?.name}</span>
            </LinkWithOrigin>
          </div>
        </div>
      )}
    </div>
  )
}
